import {z} from 'zod';
import dedent from 'dedent';
import {defineToolRegistryManifest} from '../../types/base-tool-types';

const toolName = 'edit-remote-file' as const;

const argumentZod = z.object({
  path: z
    .string()
    .describe(
      'The path of the file to edit, relative to the project root (e.g. "package.json", "component-id-1.webflow.tsx")'
    ),
  intent: z
    .string()
    .optional()
    .describe(
      'A short, human-readable summary of what this edit accomplishes (e.g. "Adding responsive breakpoints", "Fixing compilation error"). Shown to the user in the UI.'
    ),
  instruction: z
    .string()
    .describe(
      'A single sentence instruction describing what you are going to do for the edit. This is used to assist the code merging model. Use the first person to describe what you are going to do and disambiguate any uncertainty in the edit.'
    ),
  update: z
    .string()
    .describe(
      'An abbreviated edit snippet showing only the changes, using comments like "// ... existing code ..." to denote unchanged sections. Preserve indentation and final code structure. This format is optimized for fast, token-efficient code merging.'
    ),
});

export const editRemoteFileTool = defineToolRegistryManifest({
  id: toolName,
  prettyName: 'Edit component',
  prettyStatus: {
    executing: 'Editing component',
    executed: 'Edited component',
    failed: 'Failed to edit component',
    partial: 'Edited component with errors',
  },
  prettyErrors: {
    fallback: 'Unable to edit component.',
    patterns: [
      {
        match: 'not found',
        message: 'Component not found.',
      },
      {
        match: 'syntax error',
        message: 'Code contains syntax errors.',
      },
      {
        match: 'not available',
        message: 'Editing environment is not available.',
      },
    ],
  },
  llmTool: {
    name: toolName,
    description: dedent`
      Edits parts of a file in the code component sandbox environment based on a brief instruction and an abbreviated update snippet. The edit changes you write will be merged with the original file content using a fast-apply model. Use this tool when you need to make small to medium size edits to a file, without needing to rewrite the entire file. Types of edits include: adding, removing, replacing, or modifying the file's content.

      **Use 'write-remote-file' instead when**:
      - Creating a new file from scratch
      - Making very large changes (>50% of the file)
      - You need to provide completely new content

      **Guidance for using the tool:**
      - Be clear and concise and provide crucial context for ambiguous edits within your instruction.
      - Use first person in your instruction (e.g., "I am adding a new import for the utility function").
      - Specify only the precise lines of code that you wish to edit in the \`update\` field.
      - Avoid writing large blocks of unchanged code. For all unchanged code, represent it using the special comment \`// ... existing code ...\`.
      - Include only a few lines of surrounding unchanged code for context in order to resolve ambiguity on where the edit should be applied.
      - Failure to minimize the number of tokens it takes to correctly express an edit will lead to longer wait times and a worse user experience.
      - Each edit must be specified in sequence, with the special comment to represent unchanged code in between edited lines.
      - If your first edit does not start at the very beginning of the file, you must begin your \`update\` snippet with the special comment to indicate that all code before your first edit should remain unchanged. Only omit this comment if your edit starts at the very first character of the file.
      - Do not omit spans of pre-existing code or comments without using the special comment to indicate its absence. If you omit the existing code comment, the fast-apply model may inadvertently delete these lines.
      - If you plan on deleting a section, you must omit it and provide surrounding context before and after to disambiguate your intention.

      **Example of an \`update\` snippet that makes 3 edits in the middle of a file:**
      \`\`\`tsx
      // ... existing code ...
      export default function MyComponent({ title, backgroundColor }) {
      // ... existing code ...
        return <div style={{ backgroundColor }}>{title}</div>;
      // ... existing code ...
      }
      \`\`\`

      **Example of how to delete a section from the file:**
      Initial code:
      \`\`\`tsx
      function helper1() {}
      function helper2() {}
      function helper3() {}
      export default function MyComponent() {}
      \`\`\`
      Update snippet:
      \`\`\`tsx
      // ... existing code ...
      function helper1() {}
      function helper3() {}
      // ... existing code ...
      \`\`\`
    `,
    inputSchema: argumentZod,
  },
  type: 'designer',
  permission: [{resource: 'SiteSymbol', action: 'modify'}],
  confirmBeforeExecuting: false,
  timeout: 60000,
});
